import { createStyles, makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";
import { Formik, useFormikContext } from "formik";
import * as React from "react";
import * as Yup from "yup";

import {
    domain,
    getUserFromStorage,
    RejectCandidate,
    RejectCandidateFormData,
    RejectCandidateType,
    Text,
} from "../..";
import {
    Checkbox,
    DateField,
    DateInput,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Label,
    Layout,
    RelodeTheme,
    SelectableButtonItem,
    SelectableButtonsList,
    SelectField,
    Switch,
    TextField,
} from "../../components";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            textSpacingBottom: {
                marginBottom: theme.spacing(2),
            },
            initialStepContainer: {
                padding: theme.spacing(0, 2.5),
            },
            content: {
                marginBottom: theme.spacing(4),
            },
            checkBoxLabel: {
                marginTop: theme.spacing(1),
            },
        }),
    { name: "RejectContractCandidateDialog" }
);

export const rejectCandidateValidationSchema = (
    showHiredEndDate: boolean,
    hiredStartDate: Date
) =>
    Yup.object().shape({
        hiredEndDate:
            showHiredEndDate &&
            Yup.date().when("reason", {
                is: () => showHiredEndDate,
                then: Yup.date()
                    .typeError("please enter a valid date")
                    .required("please enter a termination date")
                    .min(
                        hiredStartDate,
                        `please enter a date on or after start date: ${format(
                            new Date(hiredStartDate),
                            "P"
                        )}`
                    )
                    .nullable(true),
            }),
        reason: Yup.number()
            .required("please choose a rejection reason")
            .nullable(true),
        details: Yup.string()
            .trim()
            .required("please enter notes about the rejection"),
    });

type CandidatePresentForWorkAnswers = "unknown" | "yes" | "no";

const CandidatePresentForWorkSelections = [
    {
        id: "yes",
        label: "yes",
    },
    {
        id: "no",
        label: "no",
    },
];

interface RejectCandidateDialogProps {
    candidate: RejectCandidateType;
    showInternalDetailsMessage?: boolean;
    canCreditBookings?: boolean;
    isBookingCreditConfirmationNeeded?: boolean;
    onRejectCandidate: (data: RejectCandidate) => void;
    onSetHiredEndDateForCandidate?: (hiredEndDate: Date) => void;
    onCancel: () => void;
}

export const RejectCandidateDialog = (props: RejectCandidateDialogProps) => {
    const { candidate } = props;

    return (
        <Formik
            name="rejectForm"
            initialValues={RejectCandidateFormData.create(candidate)}
            onSubmit={() => {
                /* noop */
            }}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={rejectCandidateValidationSchema(
                candidate.showHiredEndDate,
                candidate.hiredStartDate
                    ? candidate.hiredStartDate
                    : candidate.hiredEndDate
            )}
        >
            <RejectContractCandidateInnerDialog {...props} />
        </Formik>
    );
};

export const RejectContractCandidateInnerDialog = (
    props: RejectCandidateDialogProps
) => {
    const classes = useStyles({});
    const formik = useFormikContext<RejectCandidateFormData>();
    const user = getUserFromStorage();

    const {
        onRejectCandidate,
        onSetHiredEndDateForCandidate,
        onCancel,
        candidate,
        canCreditBookings,
        isBookingCreditConfirmationNeeded,
        showInternalDetailsMessage,
    } = props;

    const { values, setFieldValue, setFieldTouched } = formik;

    const [selectedPrompt, setSelectedPrompt] =
        React.useState<CandidatePresentForWorkAnswers>(
            candidate.isCandidateRejectPromptRequired ? "unknown" : "no"
        );

    const [hiredEndDateString, setHiredEndDateString] = React.useState<
        string | undefined
    >(undefined);
    const [hiredEndDateValidation, setHiredEndDateValidation] = React.useState<
        string | undefined
    >(undefined);

    const handleRejectCandidateSubmit = async () => {
        const errors = await formik.validateForm(values);
        if (Object.keys(errors).length > 0) return;

        const dataResult = RejectCandidate.create(values);
        await onRejectCandidate(dataResult);
    };

    const handleSetHiredEndDateForCandidateSubmit = async () => {
        // moved validate in here so we get required check and not immediately red
        const isValid = validateHiredEndDate();

        if (isValid && onSetHiredEndDateForCandidate) {
            const formattedHiredEndDate = new Date(
                hiredEndDateString + "T12:00:00"
            );
            await onSetHiredEndDateForCandidate(formattedHiredEndDate);
        }
    };

    const validateHiredEndDate = (): boolean => {
        let isValid = true;

        if (!hiredEndDateString) {
            setHiredEndDateValidation("please select an end date");
            isValid = false;
        } else if (
            new Date(hiredEndDateString + "T12:00:00") >=
            new Date(candidate.hiredEndDate.toDateString())
        ) {
            setHiredEndDateValidation(
                `end date must occur before current end date: ${format(
                    candidate.hiredEndDate,
                    "P"
                )}`
            );
            isValid = false;
        } else if (
            new Date(hiredEndDateString + "T12:00:00") <=
            new Date(candidate.hiredStartDate.toDateString())
        ) {
            setHiredEndDateValidation(
                `end date must occur on or after current start date: ${format(
                    candidate.hiredStartDate,
                    "P"
                )}`
            );
            isValid = false;
        }
        return isValid;
    };

    const handleCandidatePresentForWorkSelection = (
        item: SelectableButtonItem
    ) => {
        setSelectedPrompt(item.id);
    };

    const handleSwitchToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue("dnr" as keyof RejectCandidateFormData, e.target.checked);
        setFieldTouched("dnr" as keyof RejectCandidateFormData);
    };

    const handleIsBookingCreditRequestedCheckBoxToggle = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFieldValue(
            "isBookingCreditRequested" as keyof RejectCandidateFormData,
            e.target.checked
        );
        setFieldTouched(
            "isBookingCreditRequested" as keyof RejectCandidateFormData
        );
    };

    // for credit prompts to accounting
    const isUserAccountingAndCanCreditBookings =
        user.role === domain.UserRole.Accounting && canCreditBookings;

    // determine reject reasons to show based on user role and if candidate status is interview/hired
    const rejectReasons = domain.CandidateRejectedReasonType.availableReasons(
        user.role,
        candidate.isNoShowRejectionReasonEligible
    );

    // do we need to prompt if this candidate worked a day?
    const prompt = candidate.isCandidateRejectPromptRequired ? (
        <>
            <Text variant="h4" className={classes.textSpacingBottom}>
                {candidate.candidateFullName}
            </Text>
            <Text variant="h5" className={classes.textSpacingBottom}>
                Was the candidate present for at least 1 day of work?
            </Text>
            <SelectableButtonsList
                id="candidatePresentForWork"
                size="lg"
                onClick={handleCandidatePresentForWorkSelection}
                items={CandidatePresentForWorkSelections.map(selectionType => ({
                    id: selectionType.id,
                    label: selectionType.label,
                    selected: selectionType.id === selectedPrompt,
                }))}
            />
            <div className={classes.content}></div>
        </>
    ) : null;

    const rejectContent: Record<
        CandidatePresentForWorkAnswers,
        React.ReactElement
    > = {
        ["yes"]: (
            <Layout mode="100">
                <Text variant="h5">Please specify the last day of work.</Text>
                <DateInput
                    fullWidth
                    label="end date"
                    name="hiredEndDate"
                    onChange={value => setHiredEndDateString(value)}
                    value={hiredEndDateString}
                    error={!!hiredEndDateValidation}
                    helperText={hiredEndDateValidation}
                />
            </Layout>
        ),
        ["no"]: (
            <Layout mode="100">
                {candidate.isCandidateRejectPromptRequired ? (
                    <Text variant="h5">Please provide rejection details.</Text>
                ) : (
                    <Text variant="h4">{candidate.candidateFullName}</Text>
                )}
                {candidate.showHiredEndDate ? (
                    <DateField label="termination date" name="hiredEndDate" />
                ) : null}
                <SelectField
                    mode="list"
                    id="reject_reason_select"
                    data-cy="rejectReasonSelect"
                    name="reason"
                    label="reason"
                    options={rejectReasons}
                    getOptionLabel={o => o.description}
                    getFieldOption={(o, v) => o.id === v}
                    setFieldValue={o => o.id}
                />
                <TextField
                    inputProps={{ maxLength: 10240 }}
                    id="reject_details"
                    data-cy="rejectDetailsTextField"
                    label={
                        showInternalDetailsMessage
                            ? "details (sent to referring agent not self-applies)"
                            : "details (help us improve)"
                    }
                    name="details"
                    multiline={true}
                    rows={5}
                />
                {candidate.showDnr ? (
                    <Switch
                        id="reject.dnr"
                        checked={values.dnr || false}
                        onSwitchChange={handleSwitchToggle}
                        label="do not recruit"
                        labelPlacement="end"
                        color="default"
                        value={values.dnr}
                        negative
                    />
                ) : null}

                {isBookingCreditConfirmationNeeded &&
                user.role === domain.UserRole.Accounting ? (
                    isUserAccountingAndCanCreditBookings ? (
                        <Checkbox
                            id="reject.isBookingReversalNeeded"
                            checked={values.isBookingCreditRequested || false}
                            color="default"
                            label={
                                <>
                                    <Text className={classes.checkBoxLabel}>
                                        Credit bookings for this candidate
                                    </Text>
                                    <Label label="Applies to all non-zero GP pending bookings" />
                                </>
                            }
                            onChange={
                                handleIsBookingCreditRequestedCheckBoxToggle
                            }
                        />
                    ) : (
                        <Text>
                            * No bookings meet the criteria for applying credits
                        </Text>
                    )
                ) : null}
            </Layout>
        ),
        ["unknown"]: null,
    };

    const rejectActions: Record<
        CandidatePresentForWorkAnswers,
        React.ReactElement
    > = {
        ["yes"]: (
            <DialogActions
                submit={{
                    label: "save",
                    onSubmit: handleSetHiredEndDateForCandidateSubmit,
                    color: "primary",
                }}
            />
        ),
        ["no"]: (
            <DialogActions
                submit={{
                    label: "reject",
                    onSubmit: handleRejectCandidateSubmit,
                    color: "secondary",
                }}
            />
        ),
        ["unknown"]: null,
    };

    return (
        <Dialog id="rejectCandidate" open onClose={onCancel}>
            <DialogTitle title="reject candidate" onClose={onCancel} />
            <DialogContent>
                {prompt}
                {rejectContent[selectedPrompt]}
            </DialogContent>
            {rejectActions[selectedPrompt]}
        </Dialog>
    );
};
